/**
 * Custom enquiry modal. Focus trap + focusin containment keep keyboard focus in the dialog.
 * We do not set `inert` on <main> because this modal is rendered inside the product section,
 * which lives under #MainContent — inert on the main element would disable the modal too.
 */
(function () {
  const BODY_CLASS = 'custom-enquiry-modal-open';
  const SENDING_LABEL = 'Sending...';
  const BOTPOISON_WAIT_MS = 10000;
  const MODAL_TRANSITION_MS = 400;

  function formatEnquiryProductHeading(productTitle, variantTitle) {
    const base = (productTitle || '').trim();
    const v = (variantTitle || '').trim();
    if (!v || v === 'Default Title') return base;
    return base ? base + ' — ' + v : v;
  }

  function syncCustomEnquiryFromVariant(variant) {
    const title = variant?.title != null ? String(variant.title) : '';
    document.querySelectorAll('.js-custom-enquiry-variant-field').forEach((input) => {
      input.value = title;
      const modal = input.closest('[data-custom-enquiry-modal]');
      modal?.setAttribute('data-enquiry-variant-title', title);
    });
    document.querySelectorAll('.js-custom-enquiry-product-heading').forEach((el) => {
      const base = el.getAttribute('data-product-title') || '';
      el.textContent = formatEnquiryProductHeading(base, title);
    });
  }

  function reapplyEnquiryVariantAfterReset(modal) {
    if (!modal) return;
    const stored = modal.getAttribute('data-enquiry-variant-title');
    if (stored == null) return;
    const field = modal.querySelector('.js-custom-enquiry-form')?.querySelector('.js-custom-enquiry-variant-field');
    if (field) field.value = stored;
    const heading = modal.querySelector('.js-custom-enquiry-product-heading');
    if (heading) {
      const base = heading.getAttribute('data-product-title') || '';
      heading.textContent = formatEnquiryProductHeading(base, stored);
    }
  }

  document.addEventListener('theme:variant:change', (e) => {
    syncCustomEnquiryFromVariant(e.detail?.variant);
  });

  function getModalFromTrigger(btn) {
    const id = btn.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function waitForBotpoison(maxMs) {
    return new Promise((resolve) => {
      if (typeof window.Botpoison !== 'undefined') {
        resolve();
        return;
      }
      const start = Date.now();
      const t = setInterval(() => {
        if (typeof window.Botpoison !== 'undefined' || Date.now() - start > maxMs) {
          clearInterval(t);
          resolve();
        }
      }, 50);
    });
  }

  function getFocusableInModal(modal) {
    if (!modal) return [];
    const sel =
      'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(modal.querySelectorAll(sel)).filter((el) => {
      if (el.closest('[hidden]')) return false;
      return true;
    });
  }

  function restoreSubmitButton(form) {
    if (!form) return;
    const btn = form.querySelector('.js-custom-enquiry-submit');
    if (!btn) return;
    const original = btn.getAttribute('data-original-label');
    if (original) btn.textContent = original;
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }

  function setSubmitLoading(form, loading) {
    const btn = form.querySelector('.js-custom-enquiry-submit');
    if (!btn) return;
    if (loading) {
      if (!btn.getAttribute('data-original-label')) {
        btn.setAttribute('data-original-label', btn.textContent.trim());
      }
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = SENDING_LABEL;
    } else {
      restoreSubmitButton(form);
    }
  }

  function openModal(modal) {
    if (!modal) return;
    document.body.classList.add(BODY_CLASS);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          const firstField = modal.querySelector('.js-custom-enquiry-form [name="first_name"]');
          firstField?.focus({ preventScroll: true });
        });
      });
    });
  }

  function resetModalState(modal) {
    if (!modal) return;
    const form = modal.querySelector('.js-custom-enquiry-form');
    if (form) {
      restoreSubmitButton(form);
      form.reset();
      reapplyEnquiryVariantAfterReset(modal);
    }
    const errEl = modal.querySelector('.js-custom-enquiry-error');
    if (errEl) {
      errEl.textContent = '';
      errEl.hidden = true;
    }
    const formWrap = modal.querySelector('.js-enquiry-form-wrapper');
    const successWrap = modal.querySelector('.js-enquiry-success-wrapper');
    if (formWrap) formWrap.hidden = false;
    if (successWrap) successWrap.hidden = true;
  }

  function closeModal(modal) {
    if (!modal) return;
    if (modal.getAttribute('aria-hidden') === 'true') return;
    let finished = false;
    let fallbackTimer;
    function done() {
      if (finished) return;
      finished = true;
      clearTimeout(fallbackTimer);
      modal.removeEventListener('transitionend', onTransitionEnd);
      document.body.classList.remove(BODY_CLASS);
      resetModalState(modal);
    }
    function onTransitionEnd(e) {
      if (e.target !== modal || e.propertyName !== 'opacity') return;
      done();
    }
    modal.setAttribute('aria-hidden', 'true');
    modal.addEventListener('transitionend', onTransitionEnd);
    fallbackTimer = setTimeout(done, MODAL_TRANSITION_MS);
  }

  function findOpenModal() {
    return document.querySelector('[data-custom-enquiry-modal][aria-hidden="false"]');
  }

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('.js-open-custom-enquiry');
    if (openBtn) {
      e.preventDefault();
      let modal = getModalFromTrigger(openBtn);
      if (!modal) modal = document.querySelector('[data-custom-enquiry-modal]');
      openModal(modal);
      return;
    }
    const closeEl = e.target.closest('.js-close-custom-enquiry');
    if (closeEl) {
      e.preventDefault();
      const modalFromClose = closeEl.closest('[data-custom-enquiry-modal]');
      closeModal(modalFromClose || findOpenModal());
    }
  });

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.js-custom-enquiry-form');
    if (!form) return;
    e.preventDefault();
    const modal = form.closest('[data-custom-enquiry-modal]');
    if (!modal) return;
    const errEl = modal.querySelector('.js-custom-enquiry-error');
    if (errEl) {
      errEl.textContent = '';
      errEl.hidden = true;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setSubmitLoading(form, true);

    try {
      const botpoisonKey = (form.getAttribute('data-botpoison-key') || '').trim();
      let botpoisonSolution = null;

      if (botpoisonKey) {
        await waitForBotpoison(BOTPOISON_WAIT_MS);
        if (typeof window.Botpoison !== 'undefined') {
          try {
            const botpoison = new window.Botpoison({ publicKey: botpoisonKey });
            const result = await botpoison.challenge();
            botpoisonSolution = result?.solution ?? null;
          } catch (bpErr) {
            console.error('Botpoison challenge failed:', bpErr);
          }
        } else {
          console.warn('Botpoison public key is set but @botpoison/browser did not load in time.');
        }
      }

      const formData = new FormData(form);
      const formattedData = {};
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('_')) {
          formattedData[key] = value;
        } else {
          const prettyKey = key
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          formattedData[prettyKey] = value;
        }
      }
      if (botpoisonSolution) {
        formattedData._botpoison = botpoisonSolution;
      }

      const res = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formattedData),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);

      const formWrap = modal.querySelector('.js-enquiry-form-wrapper');
      const successWrap = modal.querySelector('.js-enquiry-success-wrapper');
      if (formWrap) formWrap.hidden = true;
      if (successWrap) {
        successWrap.hidden = false;
        successWrap.querySelector('.js-close-custom-enquiry')?.focus({ preventScroll: true });
      }
      restoreSubmitButton(form);
    } catch (err) {
      restoreSubmitButton(form);
      if (errEl) {
        errEl.textContent =
          'Something went wrong. Please check your connection and try again.';
        errEl.hidden = false;
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!document.body.classList.contains(BODY_CLASS)) return;
    const modal = findOpenModal();
    if (!modal) return;

    if (e.code === 'Escape') {
      e.preventDefault();
      closeModal(modal);
      return;
    }

    if (e.code !== 'Tab') return;
    const focusables = getFocusableInModal(modal);
    if (focusables.length === 0) return;
    if (focusables.length === 1) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  document.addEventListener(
    'focusin',
    (e) => {
      if (!document.body.classList.contains(BODY_CLASS)) return;
      const modal = findOpenModal();
      if (!modal || modal.contains(e.target)) return;
      const focusables = getFocusableInModal(modal);
      if (focusables.length) focusables[0].focus();
    },
    true
  );
})();
